import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, Plus, Download, Trash2, CheckCircle, XCircle,
  Send, AlertTriangle, Shield, Building2, Zap, Calendar, Eye,
  Loader2, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomerTarif595Tab = ({ customerId, customerName, token }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [insuranceInfo, setInsuranceInfo] = useState({
    insurance_gln: '',
    insurance_name: '',
    insurance_policy_number: '',
    ahv_number: '',
  });
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [tarifCodes, setTarifCodes] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [savingInsurance, setSavingInsurance] = useState(false);

  // Auto-invoice state
  const [showAutoForm, setShowAutoForm] = useState(false);
  const [autoDateFrom, setAutoDateFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [autoDateTo, setAutoDateTo] = useState(() => {
    const d = new Date(); d.setDate(0);
    return d.toISOString().split('T')[0];
  });
  const [autoRemarks, setAutoRemarks] = useState('');
  const [autoPreview, setAutoPreview] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);

  // MediData send state
  const [sendingMediData, setSendingMediData] = useState(null);

  // Create invoice form
  const [newInvoice, setNewInvoice] = useState({
    date_begin: new Date().toISOString().split('T')[0],
    date_end: new Date().toISOString().split('T')[0],
    remarks: '',
    positions: [{ tarif_code: '', tarif_name: '', date_begin: new Date().toISOString().split('T')[0], quantity: 1, unit_price: 0, amount: 0 }],
  });

  useEffect(() => {
    loadData();
  }, [customerId]);

  const loadData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, invoicesRes, insuranceRes, companiesRes, codesRes] = await Promise.all([
        axios.get(`${API}/tarif595/stats`, { headers }),
        axios.get(`${API}/tarif595/invoices?customer_id=${customerId}`, { headers }),
        axios.get(`${API}/tarif595/customer/${customerId}/insurance`, { headers }),
        axios.get(`${API}/tarif595/insurance-companies`, { headers }),
        axios.get(`${API}/tarif595/tarif-codes`, { headers }),
      ]);
      setStats(statsRes.data);
      setInvoices(invoicesRes.data);
      setInsuranceInfo(insuranceRes.data);
      setInsuranceCompanies(companiesRes.data);
      setTarifCodes(codesRes.data);
    } catch (err) {
      console.error('Load error', err);
    }
    setLoading(false);
  };

  const saveInsurance = async () => {
    setSavingInsurance(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/tarif595/customer/${customerId}/insurance`, insuranceInfo, { headers });
      toast.success('Versicherungsdaten gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
    setSavingInsurance(false);
  };

  const selectInsurance = (gln) => {
    const company = insuranceCompanies.find(c => c.gln === gln);
    setInsuranceInfo(prev => ({
      ...prev,
      insurance_gln: gln,
      insurance_name: company?.name || '',
    }));
  };

  const addPosition = () => {
    setNewInvoice(prev => ({
      ...prev,
      positions: [...prev.positions, {
        tarif_code: '', tarif_name: '', date_begin: prev.date_begin, quantity: 1, unit_price: 0, amount: 0
      }]
    }));
  };

  const updatePosition = (idx, field, value) => {
    setNewInvoice(prev => {
      const positions = [...prev.positions];
      positions[idx] = { ...positions[idx], [field]: value };
      if (field === 'tarif_code') {
        const code = tarifCodes.find(c => c.code === value);
        positions[idx].tarif_name = code?.name_de || '';
      }
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(positions[idx].quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(positions[idx].unit_price) || 0;
        positions[idx].amount = Math.round(qty * price * 100) / 100;
      }
      return { ...prev, positions };
    });
  };

  const removePosition = (idx) => {
    setNewInvoice(prev => ({
      ...prev,
      positions: prev.positions.filter((_, i) => i !== idx)
    }));
  };

  const createInvoice = async () => {
    if (newInvoice.positions.some(p => !p.tarif_code || !p.unit_price)) {
      toast.error('Bitte alle Positionen ausfuellen');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        customer_id: customerId,
        treatment_date_begin: newInvoice.date_begin,
        treatment_date_end: newInvoice.date_end,
        remarks: newInvoice.remarks,
        positions: newInvoice.positions.map((p, i) => ({
          record_id: i + 1,
          tarif_code: p.tarif_code,
          tarif_name: p.tarif_name,
          date_begin: p.date_begin,
          quantity: parseFloat(p.quantity) || 1,
          unit_price: parseFloat(p.unit_price) || 0,
          amount: parseFloat(p.amount) || 0,
          session: 1,
        })),
      };
      await axios.post(`${API}/tarif595/invoices`, payload, { headers });
      toast.success('Rechnung erstellt');
      setShowCreateForm(false);
      setNewInvoice({
        date_begin: new Date().toISOString().split('T')[0],
        date_end: new Date().toISOString().split('T')[0],
        remarks: '',
        positions: [{ tarif_code: '', tarif_name: '', date_begin: new Date().toISOString().split('T')[0], quantity: 1, unit_price: 0, amount: 0 }],
      });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const finalizeInvoice = async (id) => {
    if (!window.confirm('Rechnung finalisieren? Dies kann nicht rueckgaengig gemacht werden.')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/tarif595/invoices/${id}/finalize`, {}, { headers });
      toast.success('Rechnung finalisiert');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const cancelInvoice = async (id) => {
    if (!window.confirm('Rechnung wirklich stornieren?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/tarif595/invoices/${id}/cancel`, {}, { headers });
      toast.success('Rechnung storniert');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const downloadFile = async (invoiceId, invoiceNumber, type) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/tarif595/invoices/${invoiceId}/${type}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.${type}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type.toUpperCase()} heruntergeladen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Fehler beim ${type.toUpperCase()}-Download`);
    }
  };

  // Auto-invoice preview
  const loadAutoPreview = async () => {
    setAutoLoading(true);
    setAutoPreview(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/tarif595/invoices/auto-generate/preview`, {
        customer_id: customerId,
        date_from: autoDateFrom,
        date_to: autoDateTo,
      }, { headers });
      setAutoPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei Vorschau');
    }
    setAutoLoading(false);
  };

  // Auto-invoice generate
  const generateAutoInvoice = async () => {
    setAutoGenerating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/tarif595/invoices/auto-generate`, {
        customer_id: customerId,
        date_from: autoDateFrom,
        date_to: autoDateTo,
        remarks: autoRemarks,
      }, { headers });
      const inv = res.data.invoice;
      toast.success(`Rechnung ${inv.invoice_number} erstellt (${res.data.matched_appointments} Termine)`);
      setShowAutoForm(false);
      setAutoPreview(null);
      setAutoRemarks('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
    setAutoGenerating(false);
  };

  // MediData send
  const sendToMediData = async (invoiceId) => {
    setSendingMediData(invoiceId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/tarif595/medidata/queue/${invoiceId}`, {}, { headers });
      toast.success(res.data.status === 'sent' ? 'Erfolgreich uebermittelt (Testmodus)' : 'In Warteschlange');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei MediData-Übermittlung');
    }
    setSendingMediData(null);
  };

  const total = newInvoice.positions.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  if (loading) return <div className="text-center py-8 text-gray-500">Laden...</div>;

  if (!stats.enabled) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertTriangle size={40} className="mx-auto mb-3 text-amber-500" />
          <p className="font-medium">Tarif 595 Modul ist deaktiviert</p>
          <p className="text-sm text-gray-500 mt-1">Aktivieren Sie es unter Einstellungen &gt; Tarif 595</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-tarif595-tab">
      {/* Warnung: Platzhalter-Anbieterdaten */}
      {(stats.placeholder_fields || []).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="tarif595-tab-placeholder-warning">
          <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800">
            <strong>Achtung:</strong> Die Anbieterdaten (GLN/ZSR) enthalten noch Platzhalter-Werte — Krankenkassen lehnen diese Belege ab.
            Echte Daten unter <strong>Einstellungen → Tarif 595 → Provider</strong> hinterlegen.
          </p>
        </div>
      )}

      {/* Insurance Info */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield size={18} className="text-teal-600" />
            Versicherungsdaten
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Krankenkasse</Label>
              <Select
                value={insuranceInfo.insurance_gln}
                onValueChange={selectInsurance}
              >
                <SelectTrigger data-testid="tarif595-insurance-select">
                  <SelectValue placeholder="Krankenkasse wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {insuranceCompanies.map(c => (
                    <SelectItem key={c.gln} value={c.gln}>
                      {c.name} ({c.gln})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Policen-Nr.</Label>
              <Input
                data-testid="tarif595-policy-number"
                placeholder="Policen-Nummer"
                value={insuranceInfo.insurance_policy_number}
                onChange={e => setInsuranceInfo(prev => ({ ...prev, insurance_policy_number: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">AHV-Nummer</Label>
            <Input
              data-testid="tarif595-ahv"
              placeholder="756.1234.5678.90"
              value={insuranceInfo.ahv_number}
              onChange={e => setInsuranceInfo(prev => ({ ...prev, ahv_number: e.target.value }))}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={saveInsurance}
            disabled={savingInsurance}
            data-testid="tarif595-save-insurance"
          >
            {savingInsurance ? 'Speichern...' : 'Speichern'}
          </Button>
        </CardContent>
      </Card>

      {/* Create Invoice */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText size={18} className="text-teal-600" />
              Versicherungsbelege ({invoices.length})
            </CardTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                onClick={() => { setShowAutoForm(!showAutoForm); setShowCreateForm(false); setAutoPreview(null); }}
                data-testid="tarif595-auto-invoice-btn"
              >
                <Zap size={14} className="mr-1" />
                Auto-Rechnung
              </Button>
              <Button
                size="sm"
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => { setShowCreateForm(!showCreateForm); setShowAutoForm(false); }}
                data-testid="tarif595-create-invoice-btn"
              >
                <Plus size={14} className="mr-1" />
                Neuer Beleg
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Auto-Invoice Form */}
          {showAutoForm && (
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-4 mb-4 border border-teal-200 space-y-4" data-testid="tarif595-auto-form">
              <div className="flex items-center gap-2 text-teal-800 font-medium">
                <Zap size={16} />
                Automatische Rechnungserstellung aus Terminen
              </div>
              <p className="text-sm text-teal-700">
                Termine im gewählten Zeitraum werden automatisch mit den Tarif-Mappings abgeglichen und als Rechnung erstellt.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Von</Label>
                  <Input
                    type="date"
                    value={autoDateFrom}
                    onChange={e => setAutoDateFrom(e.target.value)}
                    data-testid="tarif595-auto-date-from"
                  />
                </div>
                <div>
                  <Label className="text-xs">Bis</Label>
                  <Input
                    type="date"
                    value={autoDateTo}
                    onChange={e => setAutoDateTo(e.target.value)}
                    data-testid="tarif595-auto-date-to"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Bemerkungen (optional)</Label>
                <Input
                  placeholder="z.B. Quartal 1/2026"
                  value={autoRemarks}
                  onChange={e => setAutoRemarks(e.target.value)}
                  data-testid="tarif595-auto-remarks"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadAutoPreview}
                  disabled={autoLoading}
                  data-testid="tarif595-auto-preview-btn"
                >
                  {autoLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}
                  Vorschau
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowAutoForm(false); setAutoPreview(null); }}
                >
                  Abbrechen
                </Button>
              </div>

              {/* Preview results */}
              {autoPreview && (
                <div className="border border-teal-300 rounded-lg bg-white p-3 space-y-3" data-testid="tarif595-auto-preview-result">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {autoPreview.total_appointments} Termine gefunden
                    </span>
                    <Badge variant="outline" className="border-teal-400 text-teal-700">
                      CHF {autoPreview.estimated_total?.toFixed(2)}
                    </Badge>
                  </div>

                  {autoPreview.matched?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-green-700 mb-1">
                        <CheckCircle size={12} className="inline mr-1" />
                        Zugeordnet ({autoPreview.matched.length})
                      </p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {autoPreview.matched.map((m, i) => (
                          <div key={i} className="flex items-center justify-between text-xs bg-green-50 rounded px-2 py-1">
                            <span>{m.date} - {m.service_name}</span>
                            <span className="font-mono text-green-700">{m.tarif_code} | CHF {m.price?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {autoPreview.unmapped?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-700 mb-1">
                        <AlertTriangle size={12} className="inline mr-1" />
                        Ohne Tarif-Mapping ({autoPreview.unmapped.length})
                      </p>
                      <div className="space-y-1 max-h-28 overflow-y-auto">
                        {autoPreview.unmapped.map((u, i) => (
                          <div key={i} className="text-xs bg-amber-50 rounded px-2 py-1 text-amber-700">
                            {u.date} - {u.service_name} (CHF {(u.price || 0).toFixed(2)})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {autoPreview.total_appointments === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">
                      Keine Termine im gewählten Zeitraum gefunden.
                    </p>
                  )}

                  {autoPreview.matched?.length > 0 && (
                    <div className="flex justify-end pt-2 border-t">
                      <Button
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700"
                        onClick={generateAutoInvoice}
                        disabled={autoGenerating}
                        data-testid="tarif595-auto-generate-btn"
                      >
                        {autoGenerating ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <ArrowRight size={14} className="mr-1" />
                        )}
                        Rechnung erstellen ({autoPreview.matched.length} Positionen)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Create Form */}
          {showCreateForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border space-y-3" data-testid="tarif595-create-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Von</Label>
                  <Input
                    type="date"
                    value={newInvoice.date_begin}
                    onChange={e => setNewInvoice(prev => ({ ...prev, date_begin: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Bis</Label>
                  <Input
                    type="date"
                    value={newInvoice.date_end}
                    onChange={e => setNewInvoice(prev => ({ ...prev, date_end: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Positionen</Label>
                {newInvoice.positions.map((pos, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      {idx === 0 && <Label className="text-xs">Tarif-Code</Label>}
                      <Select
                        value={pos.tarif_code}
                        onValueChange={v => updatePosition(idx, 'tarif_code', v)}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Code..." />
                        </SelectTrigger>
                        <SelectContent>
                          {tarifCodes.map(c => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.code} - {c.name_de}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Datum</Label>}
                      <Input
                        type="date"
                        className="h-9 text-xs"
                        value={pos.date_begin}
                        onChange={e => updatePosition(idx, 'date_begin', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1">
                      {idx === 0 && <Label className="text-xs">Menge</Label>}
                      <Input
                        type="number"
                        className="h-9 text-xs"
                        value={pos.quantity}
                        min={1}
                        onChange={e => updatePosition(idx, 'quantity', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Preis (CHF)</Label>}
                      <Input
                        type="number"
                        className="h-9 text-xs"
                        step="0.05"
                        value={pos.unit_price}
                        onChange={e => updatePosition(idx, 'unit_price', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Betrag</Label>}
                      <div className="h-9 flex items-center text-xs font-medium">
                        CHF {(parseFloat(pos.amount) || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="col-span-1">
                      {idx === 0 && <Label className="text-xs">&nbsp;</Label>}
                      {newInvoice.positions.length > 1 && (
                        <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-500" onClick={() => removePosition(idx)}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addPosition} className="text-xs">
                  <Plus size={12} className="mr-1" /> Position hinzufügen
                </Button>
              </div>

              <div>
                <Label className="text-xs">Bemerkungen (optional)</Label>
                <Input
                  placeholder="z.B. Fitness-Programm Q1 2026"
                  value={newInvoice.remarks}
                  onChange={e => setNewInvoice(prev => ({ ...prev, remarks: e.target.value }))}
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <span className="font-bold text-lg">Total: CHF {total.toFixed(2)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>Abbrechen</Button>
                  <Button
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={createInvoice}
                    data-testid="tarif595-submit-invoice"
                  >
                    Rechnung erstellen
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Invoice list */}
          {invoices.length === 0 && !showCreateForm ? (
            <div className="text-center py-6 text-gray-400">
              <FileText size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Keine Belege vorhanden</p>
            </div>
          ) : (
            <div className="space-y-2">
              {invoices.map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={inv.status === 'finalized' ? 'default' : inv.status === 'cancelled' ? 'destructive' : 'secondary'}
                      className={inv.status === 'finalized' ? 'bg-green-100 text-green-800 border-green-300' : ''}
                    >
                      {inv.status === 'draft' ? 'Entwurf' : inv.status === 'finalized' ? 'Finalisiert' : 'Storniert'}
                    </Badge>
                    <div>
                      <p className="font-medium text-sm">{inv.invoice_number}</p>
                      <p className="text-xs text-gray-500">
                        {inv.treatment_date_begin} - {inv.treatment_date_end} | CHF {inv.total_amount?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {inv.status === 'draft' && (
                      <>
                        <Button
                          variant="outline" size="sm" className="text-xs text-green-600"
                          onClick={() => finalizeInvoice(inv.id)}
                          data-testid={`tarif595-finalize-${inv.id}`}
                        >
                          <CheckCircle size={12} className="mr-1" />
                          Finalisieren
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="text-xs text-red-500"
                          onClick={() => cancelInvoice(inv.id)}
                        >
                          <XCircle size={12} />
                        </Button>
                      </>
                    )}
                    {inv.status === 'finalized' && (
                      <Button
                        variant="outline" size="sm" className="text-xs text-blue-600 border-blue-200"
                        onClick={() => sendToMediData(inv.id)}
                        disabled={sendingMediData === inv.id}
                        data-testid={`tarif595-medidata-send-${inv.id}`}
                      >
                        {sendingMediData === inv.id ? (
                          <Loader2 size={12} className="mr-1 animate-spin" />
                        ) : (
                          <Send size={12} className="mr-1" />
                        )}
                        An Kasse
                      </Button>
                    )}
                    {inv.status !== 'cancelled' && (
                      <>
                        <Button
                          variant="outline" size="sm" className="text-xs"
                          onClick={() => downloadFile(inv.id, inv.invoice_number, 'pdf')}
                          data-testid={`tarif595-download-pdf-${inv.id}`}
                        >
                          <Download size={12} className="mr-1" />
                          PDF
                        </Button>
                        <Button
                          variant="outline" size="sm" className="text-xs"
                          onClick={() => downloadFile(inv.id, inv.invoice_number, 'xml')}
                        >
                          <Download size={12} className="mr-1" />
                          XML
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerTarif595Tab;
